<script setup lang="ts">
const props = defineProps<{
  variant: SingleProductVariant;
}>();

const auth = useAuthorizeStore();

const { data: previews } = useProductPreviews();

const { mutate: enablePreview, isPending: isEnablingPreview } = useEnableProductPreview();

const hrColor = computed(() => props.variant.mainColor || null);

const expiry = computed(() => {
  const raw = previews.value?.[props.variant.sku];
  return raw ? new Date(raw) : null;
});

const previewLink = computed(() => {
  if (expiry.value && !isPast(expiry.value)) {
    return props.variant.previewLink;
  }

  return null;
});

const now = useNow();

const userIsAllowedToPreview = computed(() => {
  if (expiry.value && isBefore(expiry.value, now.value)) {
    // Preview has already been ordered and expired
    return false;
  }

  return auth.loggedIn && auth.isTeacher && !auth.isConsumer;
});

const contentFlags = computed(() => ({
  unibok: !!props.variant.formats?.includes("unibok"),
  description: props.variant.isPurchasable && !!props.variant.description,
  preview: userIsAllowedToPreview.value && !!props.variant.previewLink,
}));

const showDivider = computed(() =>
  Object.values(contentFlags.value).some((it) => it),
);
</script>

<template>
  <section class="mt-(--outer-padding) flex flex-col gap-5 bg-white/70 p-(--outer-padding) text-xl">
    <div class="cover mx-auto flex w-full flex-col gap-4 text-center">
      <!-- FIXME: These are 99% from Biblio, which is uncached. We should consider building a proxy, so that we can cache them -->
      <CrystallizeImage v-if="variant.cover?.url" :image="variant.cover" />
      <div v-else class="placeholder aspect-3/4 w-full" />
      <a
        v-if="variant.browsableLink"
        :href="variant.browsableLink"
        target="_blank"
        class="text-[smaller]"
      >
        Bla i boken
      </a>
    </div>
    <div class="details flex flex-col gap-8">
      <UiProductsDetails :variant="variant" />
      <hr v-if="showDivider" />
      <UiAboutUnibok
        v-if="contentFlags.unibok"
        class="text-base font-normal"
        :prefix="true"
        :title="variant.title?.replace(/unibok/i, '').trim()"
      />
      <div
        v-else-if="contentFlags.description"
        class="text-base font-normal"
        v-html="variant.description"
      />

      <div v-if="contentFlags.preview" class="flex flex-col gap-3 text-sm">
        <b class="text-lg font-semibold">Vil du vurdere boka?</b>
        <template v-if="previewLink">
          <p class="leading-normal">
            Du har tilgang til et digitalt vurderingseksemplar av boka.
          </p>
          <a
            :href="previewLink"
            target="_blank"
            rel="noopener"
            class="flex w-fit items-center gap-[0.4rem]"
          >
            Les vurderingseksemplar
            <UIcon name="far:external-link" size="0.75em" />
          </a>
        </template>
        <template v-else>
          <p class="leading-normal">
            Du kan få tilgang til et digitalt vurderingseksemplar av boka.
            Vurderingseksamplaret vil være tilgjengelig i tre måneder etter at
            det er aktivert.
          </p>
          <UButton
            :loading="isEnablingPreview"
            trailing
            variant="outline"
            class="w-fit rounded-full px-5 py-2 text-xs text-black no-underline ring-black-80 hover:bg-gray-10"
            @click="enablePreview(variant)"
          >
            Aktiver vurderingseksemplar
          </UButton>
        </template>
      </div>

      <UiSingleProductCta :variant="variant" />
    </div>
  </section>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

.placeholder {
  background: rgba(var(--variant-background), 0.2);
}

section {
  @media screen and (min-width: theme("screens.sm")) {
    --img-max-width: 15.625rem;
    display: grid;
    gap: 3.75rem;
    grid-template-columns: var(--img-max-width) minmax(0, 1fr);

    .cover {
      max-width: var(--img-max-width);
    }
  }

  .details {
    --inner-padding: 0.75rem;
    padding: var(--inner-padding);

    @media screen and (min-width: theme("screens.sm")) {
      --inner-padding: 0;
    }
  }

  hr {
    --hr-color: v-bind(hrColor);
    border: none;
    border-top: 1px solid var(--hr-color, rgb(var(--variant-background)));
  }
}
</style>
